import { useEffect, useRef, useState } from 'react'

/**
 * Keep a dismissed overlay mounted long enough to animate out.
 *
 * Every dialog in the kit had an entrance and no exit: `if (!open) return null`
 * unmounts the node on the same frame the button is pressed, so the box and its
 * scrim vanish between one frame and the next. An entrance that eases in and an
 * exit that cuts is not a slower animation — it reads as the dialog having been
 * torn off, and it is the single thing that made these feel abrupt however
 * carefully the way in was tuned.
 *
 * So: the caller keeps rendering while `mounted` is true, and reads `state` to
 * decide which animation runs. The node leaves once the exit has actually
 * finished — driven by `animationend` rather than a matching setTimeout, so the
 * duration lives in the stylesheet with the rest of the motion and cannot drift
 * away from it. `fallbackMs` only covers the case where no animation runs at
 * all (reduced motion, a display:none ancestor), where no event ever fires.
 *
 * @param {boolean} open
 * @param {number} [fallbackMs]  upper bound if `animationend` never arrives
 * @returns {{mounted: boolean, state: 'entering'|'leaving', ref: React.RefObject}}
 *   `ref` goes on the element that carries the exit animation.
 */
export default function useExitTransition(open, fallbackMs = 320) {
  const [mounted, setMounted] = useState(open)
  const ref = useRef(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }
    if (!mounted) return undefined

    const node = ref.current
    let done = false
    const finish = () => {
      if (done) return
      done = true
      setMounted(false)
    }

    /* Only the box's OWN animation ends the wait. A dialog contains marks and
       halos with animations of their own, and any of those bubbling up would
       cut the exit short. */
    const onEnd = (event) => {
      if (event.target === node) finish()
    }

    node?.addEventListener('animationend', onEnd)
    const timer = setTimeout(finish, fallbackMs)
    return () => {
      node?.removeEventListener('animationend', onEnd)
      clearTimeout(timer)
    }
  }, [open, mounted, fallbackMs])

  return { mounted, state: open ? 'entering' : 'leaving', ref }
}
